// Per-tool calculator schemas. Each tool declares its own inputs, formula and
// outputs so no two tools are the same calculator. Values in PHP.
const peso = (n) => "\u20b1" + Math.round(n).toLocaleString("en-PH");
const num = (n) => Math.round(n).toLocaleString("en-PH");
const pct = (n) => (n).toFixed(1) + "%";
const x = (n) => n.toFixed(1) + "\u00d7";

// reply-speed uplift benchmark (Harvard Business Review lead-response study, conservative)
const replyUplift = (hrs) => (hrs <= 1 ? 1.4 : 1 + 0.4 * Math.max(0, 1 - (hrs - 1) / 47));

export const CALC = {
  "ai-sales-agent-roi": {
    inputs: [
      { k: "vol", label: "Monthly DM volume", def: 140, min: 0, max: 3000, step: 10 },
      { k: "close", label: "Current close rate %", def: 8, min: 0, max: 100, step: 1 },
      { k: "deal", label: "Average deal (\u20b1)", def: 15000, min: 0, max: 3000000, step: 500 },
      { k: "reply", label: "Current avg reply time (hrs)", def: 6, min: 0, max: 48, step: 1 },
    ],
    compute: (v) => {
      const cur = v.vol * (v.close / 100) * v.deal;
      const proj = cur * replyUplift(v.reply);
      return [
        ["Current monthly revenue", peso(cur)],
        ["Projected monthly revenue", peso(proj)],
        ["Annual uplift", peso((proj - cur) * 12)],
        ["Payback vs a \u20b150k hire", Math.max(0, Math.round(50000 / Math.max(1, (proj - cur) / 30))) + " days"],
      ];
    },
    formula: "uplift = reply\u22641h ? 1.4 : 1 + 0.4\u00d7(1\u2212(reply\u22121)/47); revenue = volume \u00d7 close% \u00d7 deal",
    assumptions: ["Reply-speed uplift capped at 1.4\u00d7 (HBR lead-response benchmark, conservative).", "A junior sales hire is assumed at \u20b150,000/month loaded."],
  },
  "sales-rep-vs-ai": {
    inputs: [
      { k: "base", label: "Rep base salary (\u20b1/yr)", def: 600000, min: 0, max: 5000000, step: 10000 },
      { k: "oh", label: "Overhead %", def: 30, min: 0, max: 100, step: 5 },
      { k: "repCap", label: "Conversations a rep handles / mo", def: 900, min: 100, max: 8000, step: 50 },
      { k: "plan", label: "BloomCONNECT plan (\u20b1/mo)", def: 11995, min: 1495, max: 11995, step: 100 },
    ],
    compute: (v) => {
      const repCost = v.base * (1 + v.oh / 100);
      const aiCost = v.plan * 12;
      const aiCap = 20000;
      return [
        ["Annual rep cost (loaded)", peso(repCost)],
        ["Annual BloomCONNECT cost", peso(aiCost)],
        ["Annual saving", peso(repCost - aiCost)],
        ["5-year saving", peso((repCost - aiCost) * 5)],
        ["Conversation-capacity multiple", x(aiCap / v.repCap)],
      ];
    },
    formula: "repCost = base \u00d7 (1 + overhead%); aiCost = plan \u00d7 12; capacityMultiple = 20,000 / repConversations",
    assumptions: ["Loaded cost adds overhead % to base salary.", "The agent's practical monthly conversation capacity is taken as 20,000."],
  },
  "slow-dm-reply-revenue-loss": {
    inputs: [
      { k: "vol", label: "Monthly DMs", def: 300, min: 0, max: 5000, step: 10 },
      { k: "reply", label: "Avg reply time (hrs)", def: 8, min: 0, max: 48, step: 1 },
      { k: "close", label: "Close rate under 1hr %", def: 12, min: 0, max: 100, step: 1 },
      { k: "deal", label: "Deal value (\u20b1)", def: 15000, min: 0, max: 3000000, step: 500 },
    ],
    compute: (v) => {
      const fast = v.vol * (v.close / 100);
      const slow = fast / replyUplift(v.reply);
      const lost = (fast - slow) * v.deal;
      return [
        ["Deals lost to slow replies / mo", num(fast - slow)],
        ["Revenue lost / mo", peso(lost)],
        ["Revenue lost / yr", peso(lost * 12)],
      ];
    },
    formula: "lost = volume \u00d7 close% \u00d7 (1 \u2212 1/replyUplift) \u00d7 deal",
    assumptions: ["Close rate decays as reply time rises, using the HBR response-time curve."],
  },
  "cart-abandonment-dm": {
    inputs: [
      { k: "carts", label: "Abandoned carts / mo", def: 400, min: 0, max: 20000, step: 10 },
      { k: "aov", label: "Average order value (\u20b1)", def: 2500, min: 0, max: 100000, step: 100 },
      { k: "email", label: "Email recovery %", def: 9, min: 0, max: 100, step: 1 },
      { k: "dm", label: "DM recovery %", def: 27, min: 0, max: 100, step: 1 },
    ],
    compute: (v) => {
      const e = v.carts * (v.email / 100) * v.aov;
      const d = v.carts * (v.dm / 100) * v.aov;
      return [
        ["Recovered by email", peso(e)],
        ["Recovered by DM", peso(d)],
        ["Incremental revenue / mo", peso(d - e)],
        ["Incremental revenue / yr", peso((d - e) * 12)],
      ];
    },
    formula: "recovered = carts \u00d7 recovery% \u00d7 AOV; DM recovery benchmarked at roughly 3\u00d7 email",
    assumptions: ["DM recovery defaults to ~3\u00d7 the email baseline, a conservative reading of channel open rates."],
  },
  "cost-per-lead": {
    inputs: [
      { k: "spend", label: "Monthly spend (\u20b1)", def: 60000, min: 0, max: 2000000, step: 1000 },
      { k: "leads", label: "Leads", def: 300, min: 1, max: 10000, step: 10 },
      { k: "calls", label: "Calls booked", def: 90, min: 0, max: 10000, step: 5 },
      { k: "deals", label: "Deals closed", def: 20, min: 0, max: 5000, step: 1 },
      { k: "deal", label: "Deal value (\u20b1)", def: 15000, min: 0, max: 3000000, step: 500 },
    ],
    compute: (v) => [
      ["Cost per lead", peso(v.spend / v.leads)],
      ["Cost per booked call", peso(v.spend / Math.max(1, v.calls))],
      ["Customer acquisition cost", peso(v.spend / Math.max(1, v.deals))],
      ["CAC : deal ratio", x((v.spend / Math.max(1, v.deals)) / v.deal)],
    ],
    formula: "CPL = spend/leads; CAC = spend/deals; ratio = CAC/deal",
    assumptions: ["Assumes all spend is attributable to the funnel entered above."],
  },
  "va-vs-ai-calculator": {
    inputs: [
      { k: "rate", label: "VA monthly rate (\u20b1)", def: 35000, min: 0, max: 200000, step: 1000 },
      { k: "hours", label: "VA hours / week", def: 40, min: 1, max: 80, step: 1 },
      { k: "perHr", label: "Conversations a VA handles / hr", def: 12, min: 1, max: 60, step: 1 },
      { k: "plan", label: "BloomCONNECT plan (\u20b1/mo)", def: 5995, min: 1495, max: 11995, step: 100 },
    ],
    compute: (v) => {
      const va = v.rate * 12;
      const ai = v.plan * 12;
      const vaConv = v.hours * v.perHr * 4.3;
      return [
        ["Annual VA cost", peso(va)],
        ["Annual BloomCONNECT cost", peso(ai)],
        ["Cost per conversation (VA)", peso((v.rate) / Math.max(1, vaConv))],
        ["Coverage gap (hrs/week)", num(168 - v.hours) + " hrs the VA is off"],
        ["Annual delta", peso(va - ai)],
      ];
    },
    formula: "vaConversations/mo = hours/wk \u00d7 perHour \u00d7 4.3; costPerConv = monthlyRate / vaConversations",
    assumptions: ["4.3 weeks per month.", "The agent covers all 168 hours a week; the VA covers only rostered hours."],
  },
  "anthropic-api-spend-estimator": {
    inputs: [
      { k: "convos", label: "Conversations / mo", def: 500, min: 0, max: 50000, step: 50 },
      { k: "msgs", label: "Messages per conversation", def: 8, min: 1, max: 60, step: 1 },
      { k: "ctx", label: "Avg context tokens", def: 4000, min: 100, max: 100000, step: 100 },
      { k: "inPrice", label: "Input $/1M tokens", def: 3, min: 0, max: 50, step: 0.25 },
      { k: "outPrice", label: "Output $/1M tokens", def: 15, min: 0, max: 100, step: 0.5 },
    ],
    compute: (v) => {
      const calls = v.convos * v.msgs;
      const inTok = calls * v.ctx;
      const outTok = calls * 400;
      const usd = (inTok / 1e6) * v.inPrice + (outTok / 1e6) * v.outPrice;
      const php = usd * 60.8047;
      return [
        ["Monthly AI calls", num(calls)],
        ["Monthly token volume", num(inTok + outTok)],
        ["Monthly spend", peso(php) + " (\u2248 $" + usd.toFixed(0) + ")"],
        ["Cost per conversation", peso(php / Math.max(1, v.convos))],
      ];
    },
    formula: "spend = inputTokens/1M \u00d7 inPrice + outputTokens/1M \u00d7 outPrice; output assumed 400 tokens/call",
    assumptions: ["Output length assumed at 400 tokens per reply.", "FX \u20b160.8047 / USD, set 5 August 2026."],
  },
  "credit-usage-estimator": {
    inputs: [
      { k: "convos", label: "Conversations / mo", def: 200, min: 0, max: 20000, step: 10 },
      { k: "replies", label: "AI replies per conversation", def: 6, min: 1, max: 40, step: 1 },
      { k: "tools", label: "Tool calls per conversation", def: 1.2, min: 0, max: 20, step: 0.1 },
      { k: "waNumbers", label: "WhatsApp Web numbers", def: 1, min: 0, max: 20, step: 1 },
    ],
    compute: (v) => {
      const credits = v.convos * (v.replies + v.tools) + v.waNumbers * 50;
      const php = credits * 10;
      const plan = credits <= 200 ? "Growth" : credits <= 500 ? "Pro" : "Enterprise";
      return [
        ["Credits / mo", num(credits)],
        ["Cost at \u20b110 / credit", peso(php)],
        ["Plan allowance that covers it", plan],
        ["Headroom on that plan", plan === "Enterprise" ? "Negotiated" : "\u2014"],
      ];
    },
    formula: "credits = conversations \u00d7 (replies + toolCalls) + WhatsAppWebNumbers \u00d7 50",
    assumptions: ["1 credit per AI thinking cycle and per tool call.", "WhatsApp Web is 50 credits/number/month."],
  },
  "peso-dollar-pricing-converter": {
    inputs: [
      { k: "usd", label: "USD amount ($)", def: 100, min: 0, max: 100000, step: 1 },
      { k: "rate", label: "FX rate (\u20b1/$)", def: 60.8047, min: 1, max: 200, step: 0.0001 },
    ],
    compute: (v) => {
      const raw = v.usd * v.rate;
      const rounded = Math.ceil(raw / 100) * 100 - 5;
      return [
        ["Converted (raw)", peso(raw)],
        ["Rounded to \u20b1x95", peso(rounded)],
        ["Growth / Pro", "\u20b15,995 / \u20b111,995"],
      ];
    },
    formula: "\u20b1 = USD \u00d7 rate; rounded up to the next hundred, less 5",
    assumptions: ["Default FX \u20b160.8047 / USD, set 5 August 2026."],
  },
  "payback-period-calculator": {
    inputs: [
      { k: "plan", label: "Plan cost (\u20b1/mo)", def: 5995, min: 0, max: 50000, step: 100 },
      { k: "uplift", label: "Expected monthly uplift (\u20b1)", def: 40000, min: 0, max: 2000000, step: 1000 },
      { k: "deal", label: "Deal value (\u20b1)", def: 15000, min: 0, max: 3000000, step: 500 },
    ],
    compute: (v) => [
      ["Days to payback", Math.max(0, Math.round(v.plan / Math.max(1, v.uplift / 30))) + " days"],
      ["Deals needed to break even", num(Math.ceil(v.plan / Math.max(1, v.deal)))],
      ["12-month ROI multiple", x((v.uplift * 12) / Math.max(1, v.plan * 12))],
    ],
    formula: "paybackDays = planCost / (uplift/30); breakevenDeals = planCost / deal",
    assumptions: ["Uplift is spread evenly across the month."],
  },
  "dm-conversion-rate-calculator": {
    inputs: [
      { k: "dms", label: "DMs", def: 400, min: 0, max: 20000, step: 10 },
      { k: "replies", label: "Replies", def: 260, min: 0, max: 20000, step: 10 },
      { k: "qualified", label: "Qualified", def: 150, min: 0, max: 20000, step: 5 },
      { k: "booked", label: "Booked", def: 80, min: 0, max: 20000, step: 5 },
      { k: "closed", label: "Closed", def: 22, min: 0, max: 20000, step: 1 },
    ],
    compute: (v) => [
      ["Reply rate", pct((v.replies / Math.max(1, v.dms)) * 100)],
      ["Qualify rate", pct((v.qualified / Math.max(1, v.replies)) * 100)],
      ["Book rate", pct((v.booked / Math.max(1, v.qualified)) * 100)],
      ["Close rate", pct((v.closed / Math.max(1, v.booked)) * 100)],
      ["Overall DM \u2192 close", pct((v.closed / Math.max(1, v.dms)) * 100)],
    ],
    formula: "each stage rate = stage / previous stage; overall = closed / DMs",
    assumptions: ["A healthy overall DM\u2192close for warm inbound sits around 5\u20138%."],
  },
  "whatsapp-message-cost-estimator": {
    inputs: [
      { k: "marketing", label: "Marketing conversations / mo", def: 1000, min: 0, max: 100000, step: 50 },
      { k: "utility", label: "Utility conversations / mo", def: 400, min: 0, max: 100000, step: 50 },
      { k: "service", label: "Service conversations / mo", def: 600, min: 0, max: 100000, step: 50 },
    ],
    compute: (v) => {
      const c = v.marketing * 0.86 + v.utility * 0.30 + v.service * 0.0;
      return [
        ["Marketing cost", peso(v.marketing * 0.86)],
        ["Utility cost", peso(v.utility * 0.30)],
        ["Service cost", "\u20b10 (free-tier)"],
        ["Total / mo", peso(c)],
        ["Total / yr", peso(c * 12)],
      ];
    },
    formula: "cost = marketing \u00d7 \u20b10.86 + utility \u00d7 \u20b10.30 + service \u00d7 \u20b10 (Meta PH category rates, pass-through)",
    assumptions: ["Illustrative Meta PH category rates; service conversations are free-tier.", "Passed through with no mark-up."],
  },

  "comment-to-dm-revenue": {
    inputs: [
      { k: "comments", label: "Comments / mo on your posts", def: 1200, min: 0, max: 100000, step: 50 },
      { k: "toDm", label: "Comment \u2192 DM conversion %", def: 22, min: 0, max: 100, step: 1 },
      { k: "close", label: "DM \u2192 sale close %", def: 9, min: 0, max: 100, step: 1 },
      { k: "deal", label: "Average deal (\u20b1)", def: 12000, min: 0, max: 3000000, step: 500 },
    ],
    compute: (v) => {
      const dms = v.comments * (v.toDm / 100);
      const sales = dms * (v.close / 100);
      return [
        ["DMs started from comments", num(dms)],
        ["Sales / mo", num(sales)],
        ["Revenue / mo", peso(sales * v.deal)],
        ["Revenue / yr", peso(sales * v.deal * 12)],
      ];
    },
    formula: "DMs = comments \u00d7 toDM%; sales = DMs \u00d7 close%; revenue = sales \u00d7 deal",
    assumptions: ["Auto comment-to-DM captures intent within seconds, before it cools."],
  },
  "ad-spend-recovery": {
    inputs: [
      { k: "spend", label: "Monthly ad spend (\u20b1)", def: 120000, min: 0, max: 5000000, step: 5000 },
      { k: "cpl", label: "Cost per lead (\u20b1)", def: 400, min: 1, max: 100000, step: 50 },
      { k: "nowClose", label: "Close % today", def: 6, min: 0, max: 100, step: 1 },
      { k: "aiClose", label: "Close % with instant AI follow-up", def: 11, min: 0, max: 100, step: 1 },
      { k: "deal", label: "Deal value (\u20b1)", def: 15000, min: 0, max: 3000000, step: 500 },
    ],
    compute: (v) => {
      const leads = v.spend / v.cpl;
      const now = leads * (v.nowClose / 100) * v.deal;
      const ai = leads * (v.aiClose / 100) * v.deal;
      return [
        ["Leads from spend / mo", num(leads)],
        ["Revenue today", peso(now)],
        ["Revenue with AI follow-up", peso(ai)],
        ["Recovered revenue / mo", peso(ai - now)],
        ["ROAS lift", x(ai / Math.max(1, now))],
      ];
    },
    formula: "leads = spend / CPL; revenue = leads \u00d7 close% \u00d7 deal; recovered = AI\u2212today",
    assumptions: ["Same spend and CPL; only the close rate changes with faster follow-up."],
  },
  "high-ticket-close-rate": {
    inputs: [
      { k: "leads", label: "Qualified leads / mo", def: 40, min: 0, max: 5000, step: 1 },
      { k: "now", label: "Current close %", def: 20, min: 0, max: 100, step: 1 },
      { k: "target", label: "Target close %", def: 30, min: 0, max: 100, step: 1 },
      { k: "deal", label: "High-ticket deal (\u20b1)", def: 180000, min: 0, max: 10000000, step: 5000 },
    ],
    compute: (v) => {
      const dNow = v.leads * (v.now / 100);
      const dTgt = v.leads * (v.target / 100);
      return [
        ["Deals now / mo", num(dNow)],
        ["Deals at target / mo", num(dTgt)],
        ["Extra deals / mo", num(dTgt - dNow)],
        ["Extra revenue / mo", peso((dTgt - dNow) * v.deal)],
        ["Extra revenue / yr", peso((dTgt - dNow) * v.deal * 12)],
      ];
    },
    formula: "deals = leads \u00d7 close%; extra = (target% \u2212 current%) \u00d7 leads \u00d7 deal",
    assumptions: ["Lead volume held constant; only close rate improves."],
  },
  "discovery-call-booking-rate": {
    inputs: [
      { k: "dms", label: "DMs / mo", def: 300, min: 0, max: 20000, step: 10 },
      { k: "now", label: "Current DM \u2192 call booking %", def: 10, min: 0, max: 100, step: 1 },
      { k: "target", label: "Booking % with AI", def: 18, min: 0, max: 100, step: 1 },
      { k: "show", label: "Show-up %", def: 70, min: 0, max: 100, step: 1 },
      { k: "close", label: "Close % of shows", def: 35, min: 0, max: 100, step: 1 },
      { k: "deal", label: "Programme price (\u20b1)", def: 90000, min: 0, max: 5000000, step: 1000 },
    ],
    compute: (v) => {
      const closes = (b) => v.dms * (b / 100) * (v.show / 100) * (v.close / 100);
      const cNow = closes(v.now), cTgt = closes(v.target);
      return [
        ["Calls booked now / mo", num(v.dms * (v.now / 100))],
        ["Calls booked with AI / mo", num(v.dms * (v.target / 100))],
        ["Extra enrolments / mo", num(cTgt - cNow)],
        ["Extra revenue / mo", peso((cTgt - cNow) * v.deal)],
      ];
    },
    formula: "enrolments = DMs \u00d7 booking% \u00d7 show% \u00d7 close%; extra = target\u2212current",
    assumptions: ["Show and close rates held equal so the booking-rate lift is isolated."],
  },
  "aov-improvement": {
    inputs: [
      { k: "orders", label: "Orders / mo", def: 500, min: 0, max: 100000, step: 10 },
      { k: "aov", label: "Current AOV (\u20b1)", def: 2200, min: 0, max: 500000, step: 100 },
      { k: "attach", label: "Upsell attach rate %", def: 25, min: 0, max: 100, step: 1 },
      { k: "upsell", label: "Average upsell value (\u20b1)", def: 900, min: 0, max: 200000, step: 100 },
    ],
    compute: (v) => {
      const lift = (v.attach / 100) * v.upsell;
      const newAov = v.aov + lift;
      return [
        ["New effective AOV", peso(newAov)],
        ["AOV uplift", peso(lift)],
        ["Incremental revenue / mo", peso(lift * v.orders)],
        ["Incremental revenue / yr", peso(lift * v.orders * 12)],
      ];
    },
    formula: "AOV uplift = attach% \u00d7 upsell value; incremental = uplift \u00d7 orders",
    assumptions: ["The agent offers the relevant upsell in-chat at the point of purchase."],
  },
  "24-7-revenue": {
    inputs: [
      { k: "after", label: "After-hours conversations / mo", def: 220, min: 0, max: 50000, step: 10 },
      { k: "lostNow", label: "% currently lost after hours", def: 70, min: 0, max: 100, step: 1 },
      { k: "close", label: "Close % when answered", def: 10, min: 0, max: 100, step: 1 },
      { k: "deal", label: "Deal value (\u20b1)", def: 15000, min: 0, max: 3000000, step: 500 },
    ],
    compute: (v) => {
      const captured = v.after * (v.lostNow / 100);
      const rev = captured * (v.close / 100) * v.deal;
      return [
        ["After-hours convos recovered / mo", num(captured)],
        ["Extra deals / mo", num(captured * (v.close / 100))],
        ["Revenue captured / mo", peso(rev)],
        ["Revenue captured / yr", peso(rev * 12)],
      ];
    },
    formula: "recovered = afterHours \u00d7 lost%; revenue = recovered \u00d7 close% \u00d7 deal",
    assumptions: ["A 24/7 agent answers the conversations that currently go unanswered overnight."],
  },
  "timezone-lost-sales": {
    inputs: [
      { k: "intl", label: "International enquiries / mo", def: 180, min: 0, max: 50000, step: 10 },
      { k: "outside", label: "% arriving outside your hours", def: 55, min: 0, max: 100, step: 1 },
      { k: "close", label: "Close % if answered fast", def: 9, min: 0, max: 100, step: 1 },
      { k: "deal", label: "Deal value (\u20b1)", def: 22000, min: 0, max: 3000000, step: 500 },
    ],
    compute: (v) => {
      const missed = v.intl * (v.outside / 100);
      const lost = missed * (v.close / 100) * v.deal;
      return [
        ["Enquiries hitting a closed inbox / mo", num(missed)],
        ["Deals lost to timezone / mo", num(missed * (v.close / 100))],
        ["Revenue lost / mo", peso(lost)],
        ["Revenue recoverable / yr", peso(lost * 12)],
      ];
    },
    formula: "missed = intl \u00d7 outsideHours%; lost = missed \u00d7 close% \u00d7 deal",
    assumptions: ["Assumes international buyers do not wait until your local morning to buy."],
  },
  "booking-show-up-rate": {
    inputs: [
      { k: "bookings", label: "Bookings / mo", def: 120, min: 0, max: 20000, step: 5 },
      { k: "now", label: "Current show-up %", def: 60, min: 0, max: 100, step: 1 },
      { k: "target", label: "Show-up % with reminders", def: 80, min: 0, max: 100, step: 1 },
      { k: "close", label: "Close % of shows", def: 30, min: 0, max: 100, step: 1 },
      { k: "deal", label: "Deal value (\u20b1)", def: 45000, min: 0, max: 5000000, step: 1000 },
    ],
    compute: (v) => {
      const extraShows = v.bookings * ((v.target - v.now) / 100);
      const extraDeals = extraShows * (v.close / 100);
      return [
        ["Extra attended / mo", num(extraShows)],
        ["Extra closes / mo", num(extraDeals)],
        ["Extra revenue / mo", peso(extraDeals * v.deal)],
        ["Extra revenue / yr", peso(extraDeals * v.deal * 12)],
      ];
    },
    formula: "extraShows = bookings \u00d7 (target% \u2212 current%); extraRevenue = extraShows \u00d7 close% \u00d7 deal",
    assumptions: ["In-thread reminders lift show-up without extra staff time."],
  },
  "cold-dm-outreach-roi": {
    inputs: [
      { k: "sent", label: "Cold DMs sent / mo", def: 2000, min: 0, max: 200000, step: 100 },
      { k: "reply", label: "Reply %", def: 12, min: 0, max: 100, step: 1 },
      { k: "meeting", label: "Reply \u2192 meeting %", def: 20, min: 0, max: 100, step: 1 },
      { k: "close", label: "Meeting \u2192 close %", def: 25, min: 0, max: 100, step: 1 },
      { k: "deal", label: "Deal value (\u20b1)", def: 30000, min: 0, max: 5000000, step: 1000 },
      { k: "cost", label: "Outreach cost / mo (\u20b1)", def: 20000, min: 0, max: 1000000, step: 1000 },
    ],
    compute: (v) => {
      const replies = v.sent * (v.reply / 100);
      const meetings = replies * (v.meeting / 100);
      const deals = meetings * (v.close / 100);
      const rev = deals * v.deal;
      return [
        ["Replies / mo", num(replies)],
        ["Meetings / mo", num(meetings)],
        ["Deals / mo", num(deals)],
        ["Revenue / mo", peso(rev)],
        ["ROI on outreach cost", x(rev / Math.max(1, v.cost))],
      ];
    },
    formula: "deals = sent \u00d7 reply% \u00d7 meeting% \u00d7 close%; ROI = revenue / cost",
    assumptions: ["Respect platform limits; the agent personalises at volume without spamming."],
  },
  "whatsapp-marketing-roi": {
    inputs: [
      { k: "recips", label: "Broadcast recipients / mo", def: 5000, min: 0, max: 500000, step: 100 },
      { k: "convCost", label: "Cost per marketing conversation (\u20b1)", def: 0.86, min: 0, max: 20, step: 0.01 },
      { k: "reply", label: "Reply / engagement %", def: 8, min: 0, max: 100, step: 1 },
      { k: "close", label: "Close % of engaged", def: 12, min: 0, max: 100, step: 1 },
      { k: "deal", label: "Deal value (\u20b1)", def: 2500, min: 0, max: 1000000, step: 100 },
    ],
    compute: (v) => {
      const cost = v.recips * v.convCost;
      const deals = v.recips * (v.reply / 100) * (v.close / 100);
      const rev = deals * v.deal;
      return [
        ["Send cost / mo", peso(cost)],
        ["Deals / mo", num(deals)],
        ["Revenue / mo", peso(rev)],
        ["ROI", x(rev / Math.max(1, cost))],
      ];
    },
    formula: "cost = recipients \u00d7 convCost; revenue = recipients \u00d7 reply% \u00d7 close% \u00d7 deal",
    assumptions: ["Meta marketing-category conversation pricing; the agent handles every reply."],
  },
  "bloomconnect-vs-manychat-cost": {
    inputs: [
      { k: "contacts", label: "Active contacts", def: 5000, min: 0, max: 1000000, step: 500 },
      { k: "per1k", label: "ManyChat \u20b1 per 1,000 contacts / mo", def: 900, min: 0, max: 100000, step: 50 },
      { k: "addons", label: "ManyChat add-ons / mo (\u20b1)", def: 3000, min: 0, max: 200000, step: 500 },
      { k: "plan", label: "BloomCONNECT plan (\u20b1/mo)", def: 5995, min: 1495, max: 11995, step: 100 },
    ],
    compute: (v) => {
      const mc = (v.contacts / 1000) * v.per1k + v.addons;
      return [
        ["ManyChat monthly", peso(mc)],
        ["BloomCONNECT monthly", peso(v.plan)],
        ["Monthly saving", peso(mc - v.plan)],
        ["Annual saving", peso((mc - v.plan) * 12)],
      ];
    },
    formula: "manychat = (contacts/1000) \u00d7 per1k + addons; saving = manychat \u2212 plan",
    assumptions: ["ManyChat bills by contact tier plus add-ons; BloomCONNECT is a flat plan."],
  },
  "bloomconnect-vs-gohighlevel-matrix": {
    inputs: [
      { k: "seats", label: "GoHighLevel seats", def: 3, min: 1, max: 200, step: 1 },
      { k: "seatCost", label: "GHL cost per seat / mo (\u20b1)", def: 5200, min: 0, max: 100000, step: 100 },
      { k: "addons", label: "GHL add-ons / mo (\u20b1)", def: 4000, min: 0, max: 300000, step: 500 },
      { k: "plan", label: "BloomCONNECT plan (\u20b1/mo)", def: 11995, min: 1495, max: 11995, step: 100 },
    ],
    compute: (v) => {
      const ghl = v.seats * v.seatCost + v.addons;
      return [
        ["GoHighLevel monthly", peso(ghl)],
        ["BloomCONNECT monthly", peso(v.plan)],
        ["Monthly saving", peso(ghl - v.plan)],
        ["Annual saving", peso((ghl - v.plan) * 12)],
        ["Cost per seat delta", peso(ghl / Math.max(1, v.seats) - v.plan / Math.max(1, v.seats))],
      ];
    },
    formula: "ghl = seats \u00d7 seatCost + addons; saving = ghl \u2212 plan",
    assumptions: ["GHL scales per seat; BloomCONNECT includes multi-seat within the plan."],
  },
  "multi-channel-inbox-compare": {
    inputs: [
      { k: "channels", label: "Channels you run", def: 4, min: 1, max: 7, step: 1 },
      { k: "perChannel", label: "Cost per point-tool / mo (\u20b1)", def: 2500, min: 0, max: 100000, step: 100 },
      { k: "seats", label: "Seats", def: 3, min: 1, max: 100, step: 1 },
      { k: "plan", label: "BloomCONNECT plan (\u20b1/mo)", def: 5995, min: 1495, max: 11995, step: 100 },
    ],
    compute: (v) => {
      const siloed = v.channels * v.perChannel * Math.max(1, Math.ceil(v.seats / 2));
      return [
        ["Siloed stack / mo", peso(siloed)],
        ["Unified inbox / mo", peso(v.plan)],
        ["Tools consolidated", num(v.channels) + " \u2192 1"],
        ["Monthly saving", peso(siloed - v.plan)],
        ["Annual saving", peso((siloed - v.plan) * 12)],
      ];
    },
    formula: "siloed = channels \u00d7 perTool \u00d7 ceil(seats/2); saving = siloed \u2212 plan",
    assumptions: ["Separate per-channel tools duplicate seat costs; one inbox does not."],
  },
  "whatsapp-bsp-comparison": {
    inputs: [
      { k: "convos", label: "WhatsApp conversations / mo", def: 4000, min: 0, max: 500000, step: 100 },
      { k: "markup", label: "BSP mark-up per conversation (\u20b1)", def: 0.35, min: 0, max: 20, step: 0.01 },
      { k: "platform", label: "BSP platform fee / mo (\u20b1)", def: 3500, min: 0, max: 200000, step: 100 },
      { k: "plan", label: "BloomCONNECT plan (\u20b1/mo)", def: 5995, min: 1495, max: 11995, step: 100 },
    ],
    compute: (v) => {
      const bsp = v.convos * v.markup + v.platform;
      return [
        ["BSP mark-up + fee / mo", peso(bsp)],
        ["BloomCONNECT / mo (pass-through)", peso(v.plan)],
        ["Monthly saving", peso(bsp - v.plan)],
        ["Annual saving", peso((bsp - v.plan) * 12)],
      ];
    },
    formula: "bsp = conversations \u00d7 markup + platformFee; BloomCONNECT passes Meta rates through at cost",
    assumptions: ["Meta conversation charges apply to both; only the BSP mark-up differs."],
  },
  "build-vs-buy-ai-agent": {
    inputs: [
      { k: "salary", label: "Developer salary (\u20b1/mo)", def: 120000, min: 0, max: 2000000, step: 5000 },
      { k: "months", label: "Months to build v1", def: 4, min: 1, max: 36, step: 1 },
      { k: "maint", label: "Ongoing maintenance %/yr of build", def: 30, min: 0, max: 200, step: 5 },
      { k: "plan", label: "BloomCONNECT plan (\u20b1/mo)", def: 11995, min: 1495, max: 11995, step: 100 },
    ],
    compute: (v) => {
      const build = v.salary * v.months;
      const maint = build * (v.maint / 100);
      const buyYr1 = v.plan * 12;
      return [
        ["Build cost (v1)", peso(build)],
        ["Ongoing maintenance / yr", peso(maint)],
        ["Buy: BloomCONNECT / yr", peso(buyYr1)],
        ["Year-1 delta (build \u2212 buy)", peso(build + maint - buyYr1)],
      ];
    },
    formula: "build = salary \u00d7 months; maintenance = build \u00d7 maint%; buy = plan \u00d7 12",
    assumptions: ["Build excludes infra, model spend and opportunity cost, so it is a floor."],
  },
  "ai-sales-stack-cost": {
    inputs: [
      { k: "tools", label: "Number of point tools", def: 5, min: 1, max: 40, step: 1 },
      { k: "avg", label: "Average tool cost / mo (\u20b1)", def: 2600, min: 0, max: 100000, step: 100 },
      { k: "intHrs", label: "Integration upkeep hrs / mo", def: 8, min: 0, max: 200, step: 1 },
      { k: "rate", label: "Hourly rate (\u20b1)", def: 700, min: 0, max: 20000, step: 50 },
      { k: "plan", label: "BloomCONNECT plan (\u20b1/mo)", def: 5995, min: 1495, max: 11995, step: 100 },
    ],
    compute: (v) => {
      const stack = v.tools * v.avg + v.intHrs * v.rate;
      return [
        ["Stack cost / mo", peso(stack)],
        ["Stack cost / yr", peso(stack * 12)],
        ["BloomCONNECT / yr", peso(v.plan * 12)],
        ["Annual saving", peso((stack - v.plan) * 12)],
      ];
    },
    formula: "stack = tools \u00d7 avgCost + upkeepHrs \u00d7 rate; saving = (stack \u2212 plan) \u00d7 12",
    assumptions: ["Integration upkeep is the hidden cost of a multi-tool stack."],
  },
  "chatgpt-api-vs-byok": {
    inputs: [
      { k: "convos", label: "Conversations / mo", def: 800, min: 0, max: 100000, step: 50 },
      { k: "actions", label: "AI actions per conversation", def: 7, min: 1, max: 60, step: 1 },
      { k: "tokens", label: "Avg tokens per action", def: 3500, min: 100, max: 100000, step: 100 },
      { k: "apiPrice", label: "Direct API blended $/1M tokens", def: 6, min: 0, max: 100, step: 0.25 },
      { k: "credit", label: "BloomCONNECT credits / action", def: 0.25, min: 0.05, max: 5, step: 0.05 },
    ],
    compute: (v) => {
      const acts = v.convos * v.actions;
      const apiUsd = (acts * v.tokens / 1e6) * v.apiPrice;
      const apiPhp = apiUsd * 60.8047;
      const bloomPhp = acts * v.credit * 10;
      return [
        ["AI actions / mo", num(acts)],
        ["Direct API spend", peso(apiPhp) + " (\u2248 $" + apiUsd.toFixed(0) + ")"],
        ["BloomCONNECT credit cost", peso(bloomPhp)],
        ["Cheaper by / mo", peso(Math.abs(apiPhp - bloomPhp)) + (bloomPhp < apiPhp ? " (BloomCONNECT)" : " (direct API)")],
      ];
    },
    formula: "apiSpend = actions \u00d7 tokens/1M \u00d7 price \u00d7 FX; bloom = actions \u00d7 credits \u00d7 \u20b110",
    assumptions: ["FX \u20b160.8047/USD; blended price mixes input and output tokens.", "1 credit = \u20b110."],
  },
  "dm-tool-stack-consolidation": {
    inputs: [
      { k: "scheduler", label: "Scheduler / calendar / mo (\u20b1)", def: 900, min: 0, max: 50000, step: 100 },
      { k: "chatbot", label: "Chatbot builder / mo (\u20b1)", def: 2500, min: 0, max: 50000, step: 100 },
      { k: "crm", label: "CRM / mo (\u20b1)", def: 3000, min: 0, max: 100000, step: 100 },
      { k: "inbox", label: "Shared inbox / mo (\u20b1)", def: 1800, min: 0, max: 50000, step: 100 },
      { k: "plan", label: "BloomCONNECT plan (\u20b1/mo)", def: 5995, min: 1495, max: 11995, step: 100 },
    ],
    compute: (v) => {
      const current = v.scheduler + v.chatbot + v.crm + v.inbox;
      const count = [v.scheduler, v.chatbot, v.crm, v.inbox].filter((x) => x > 0).length;
      return [
        ["Current stack / mo", peso(current)],
        ["Tools replaced", num(count) + " \u2192 1"],
        ["BloomCONNECT / mo", peso(v.plan)],
        ["Monthly saving", peso(current - v.plan)],
        ["Annual saving", peso((current - v.plan) * 12)],
      ];
    },
    formula: "current = scheduler + chatbot + crm + inbox; saving = current \u2212 plan",
    assumptions: ["Booking, bot, CRM and inbox are native to BloomCONNECT."],
  },
  "credit-top-up-planner": {
    inputs: [
      { k: "included", label: "Plan included credits / mo", def: 200, min: 0, max: 100000, step: 10 },
      { k: "usage", label: "Expected usage credits / mo", def: 320, min: 0, max: 100000, step: 10 },
      { k: "packSize", label: "Top-up pack size (credits)", def: 100, min: 10, max: 10000, step: 10 },
      { k: "packPrice", label: "Top-up pack price (\u20b1)", def: 650, min: 0, max: 500000, step: 50 },
    ],
    compute: (v) => {
      const over = Math.max(0, v.usage - v.included);
      const packs = Math.ceil(over / v.packSize);
      const topup = packs * v.packPrice;
      return [
        ["Overage credits / mo", num(over)],
        ["Top-up packs needed", num(packs)],
        ["Top-up cost / mo", peso(topup)],
        ["Effective \u20b1 / credit (overage)", over > 0 ? peso(topup / over) : "\u2014"],
      ];
    },
    formula: "overage = max(0, usage \u2212 included); packs = ceil(overage / packSize)",
    assumptions: ["Top-ups round up to whole packs; unused pack credits carry within the month."],
  },
  "conversation-volume-forecaster": {
    inputs: [
      { k: "now", label: "Conversations this month", def: 400, min: 0, max: 500000, step: 10 },
      { k: "growth", label: "Monthly growth %", def: 12, min: -50, max: 200, step: 1 },
      { k: "months", label: "Forecast horizon (months)", def: 6, min: 1, max: 36, step: 1 },
      { k: "perConv", label: "Credits per conversation", def: 7, min: 0.5, max: 100, step: 0.5 },
    ],
    compute: (v) => {
      const fut = v.now * Math.pow(1 + v.growth / 100, v.months);
      const credits = fut * v.perConv;
      const plan = credits <= 1400 ? "Growth" : credits <= 4000 ? "Pro" : "Enterprise";
      return [
        ["Conversations in month " + v.months, num(fut)],
        ["Credits needed that month", num(credits)],
        ["Recommended plan then", plan],
        ["Growth multiple over today", x(fut / Math.max(1, v.now))],
      ];
    },
    formula: "future = now \u00d7 (1 + growth%)^months; credits = future \u00d7 perConversation",
    assumptions: ["Compound monthly growth; plan thresholds are indicative allowances."],
  },
  "team-seat-planner": {
    inputs: [
      { k: "convos", label: "Conversations / mo", def: 2000, min: 0, max: 500000, step: 50 },
      { k: "perAgent", label: "Conversations an agent handles / mo", def: 900, min: 50, max: 10000, step: 50 },
      { k: "included", label: "Seats included in plan", def: 3, min: 0, max: 100, step: 1 },
      { k: "extraSeat", label: "Extra seat cost / mo (\u20b1)", def: 1500, min: 0, max: 50000, step: 100 },
    ],
    compute: (v) => {
      const needed = Math.ceil(v.convos / v.perAgent);
      const extra = Math.max(0, needed - v.included);
      return [
        ["Human-equivalent seats needed", num(needed)],
        ["Seats beyond plan", num(extra)],
        ["Extra seat cost / mo", peso(extra * v.extraSeat)],
        ["With AI handling tier-1", "Often 0\u20131 extra seats"],
      ];
    },
    formula: "seatsNeeded = ceil(conversations / perAgentCapacity); extra = max(0, needed \u2212 included)",
    assumptions: ["Manual capacity per agent; the agent absorbs most tier-1 volume, cutting seats."],
  },
  "channel-cost-estimator": {
    inputs: [
      { k: "wa", label: "WhatsApp conversations / mo", def: 1500, min: 0, max: 500000, step: 50 },
      { k: "sms", label: "SMS sent / mo", def: 800, min: 0, max: 500000, step: 50 },
      { k: "email", label: "Emails sent / mo", def: 5000, min: 0, max: 1000000, step: 100 },
      { k: "waRate", label: "WhatsApp \u20b1 / conversation", def: 0.55, min: 0, max: 20, step: 0.01 },
      { k: "smsRate", label: "SMS \u20b1 / message", def: 0.80, min: 0, max: 20, step: 0.01 },
    ],
    compute: (v) => {
      const waC = v.wa * v.waRate, smsC = v.sms * v.smsRate, emailC = (v.email / 1000) * 25;
      const total = waC + smsC + emailC;
      return [
        ["WhatsApp cost / mo", peso(waC)],
        ["SMS cost / mo", peso(smsC)],
        ["Email cost / mo", peso(emailC)],
        ["Total messaging cost / mo", peso(total)],
        ["Total / yr", peso(total * 12)],
      ];
    },
    formula: "total = WA\u00d7rate + SMS\u00d7rate + (emails/1000)\u00d7\u20b125",
    assumptions: ["Email assumed \u20b125 per 1,000 sends; rates are pass-through, no mark-up."],
  },
  "response-time-sla-planner": {
    inputs: [
      { k: "volume", label: "Conversations / mo", def: 3000, min: 0, max: 500000, step: 100 },
      { k: "sla", label: "First-response SLA (minutes)", def: 5, min: 1, max: 240, step: 1 },
      { k: "perHr", label: "Convos a human clears / hr", def: 10, min: 1, max: 100, step: 1 },
      { k: "hours", label: "Coverage hours / day needed", def: 24, min: 1, max: 24, step: 1 },
      { k: "wage", label: "Agent wage (\u20b1/hr)", def: 220, min: 0, max: 10000, step: 10 },
    ],
    compute: (v) => {
      const perDay = v.volume / 30;
      const staffHrs = perDay / v.perHr;
      const concurrency = Math.ceil(staffHrs / v.hours * (v.hours / 8));
      const monthlyWage = Math.ceil(staffHrs) * v.wage * 30;
      return [
        ["Conversations / day", num(perDay)],
        ["Human staff-hours / day for SLA", num(staffHrs)],
        ["Rough headcount for coverage", num(Math.max(1, concurrency))],
        ["Manual staffing cost / mo", peso(monthlyWage)],
        ["AI first-response cost", "\u20b10 extra \u2014 instant, 24/7"],
      ];
    },
    formula: "staffHours/day = (volume/30) / clearedPerHour; cost = staffHours \u00d7 wage \u00d7 30",
    assumptions: ["A sub-5-minute SLA overnight is unaffordable manually; the agent meets it instantly."],
  },
  "multi-location-rollout-planner": {
    inputs: [
      { k: "locations", label: "Locations / branches", def: 8, min: 1, max: 1000, step: 1 },
      { k: "perLoc", label: "Point tools per location / mo (\u20b1)", def: 3500, min: 0, max: 200000, step: 100 },
      { k: "central", label: "Central BloomCONNECT plan (\u20b1/mo)", def: 11995, min: 1495, max: 11995, step: 100 },
      { k: "weeksEach", label: "Manual setup weeks per location", def: 2, min: 0, max: 52, step: 1 },
    ],
    compute: (v) => {
      const decentralised = v.locations * v.perLoc;
      return [
        ["Decentralised tooling / mo", peso(decentralised)],
        ["Central rollout / mo", peso(v.central)],
        ["Monthly saving", peso(decentralised - v.central)],
        ["Annual saving", peso((decentralised - v.central) * 12)],
        ["Rollout time saved", num(v.locations * v.weeksEach) + " weeks \u2192 days"],
      ];
    },
    formula: "decentralised = locations \u00d7 perLocationTools; saving = decentralised \u2212 central",
    assumptions: ["One central workspace with per-location routing replaces per-branch tools."],
  },
  "ai-credit-vs-byok-breakeven": {
    inputs: [
      { k: "actions", label: "AI actions / mo", def: 8000, min: 0, max: 5000000, step: 100 },
      { k: "creditPer", label: "Credits per action", def: 0.25, min: 0.05, max: 5, step: 0.05 },
      { k: "creditPrice", label: "\u20b1 per credit", def: 10, min: 0.1, max: 100, step: 0.05 },
      { k: "byokPer", label: "BYOK token cost per action (\u20b1)", def: 0.9, min: 0, max: 100, step: 0.1 },
      { k: "byokBase", label: "BYOK base / infra fee / mo (\u20b1)", def: 2500, min: 0, max: 200000, step: 100 },
    ],
    compute: (v) => {
      const credit = v.actions * v.creditPer * v.creditPrice;
      const byok = v.actions * v.byokPer + v.byokBase;
      const perActionCredit = v.creditPer * v.creditPrice;
      const be = perActionCredit > v.byokPer ? Math.ceil(v.byokBase / (perActionCredit - v.byokPer)) : null;
      return [
        ["Credit-based cost / mo", peso(credit)],
        ["BYOK cost / mo", peso(byok)],
        ["Cheaper option", credit < byok ? "Credits" : "BYOK"],
        ["Break-even actions / mo", be ? num(be) : "Credits cheaper at any volume"],
      ];
    },
    formula: "credit = actions \u00d7 creditsPerAction \u00d7 pricePerCredit; byok = actions \u00d7 tokenCost + baseFee",
    assumptions: ["BYOK moves model spend to your own key; base fee covers infra and monitoring."],
  },
};

// Per-niche funnel calculators with their own stage names and terminal value.
export const NICHE = {
  "real-estate-lead-calculator": { stages: ["Enquiries", "Qualified buyers", "Viewings booked", "Viewings attended", "Closings"], defs: [200, 45, 55, 80, 20], deal: 4000000, dealLabel: "Property value \u00d7 3% commission", commission: 0.03 },
  "coaching-discovery-call-calculator": { stages: ["DMs", "Discovery calls booked", "Attended", "Enrolled"], defs: [300, 30, 80, 35], deal: 120000, dealLabel: "Programme price" },
  "ecommerce-cart-recovery-dm": { stages: ["Carts abandoned", "DM delivered", "Opened", "Recovered"], defs: [800, 90, 78, 27], deal: 2500, dealLabel: "AOV" },
  "restaurant-reservation-calculator": { stages: ["Enquiries", "Reservations", "Covers", "Seated (less no-shows)"], defs: [500, 60, 300, 81], deal: 1200, dealLabel: "Spend per cover" },
  "fitness-coach-dm-funnel": { stages: ["DMs", "Trials booked", "Attended", "Converted"], defs: [400, 25, 70, 40], deal: 24000, dealLabel: "LTV (MRR \u00d7 retention)" },
  "course-creator-enrolment": { stages: ["DMs", "Applications", "Calls", "Enrolments"], defs: [600, 20, 60, 40], deal: 60000, dealLabel: "Course price" },
  "dental-practice-booking": { stages: ["Enquiries", "New-patient appts", "Attended", "Plans accepted"], defs: [250, 40, 85, 55], deal: 60000, dealLabel: "Treatment + recall LTV" },
  "med-spa-treatment-booking": { stages: ["Enquiries", "Consults", "First treatments", "Course completions"], defs: [300, 50, 60, 45], deal: 120000, dealLabel: "Package value" },
  "saas-demo-booking-calculator": { stages: ["Chats", "Demos booked", "Attended", "Paid"], defs: [500, 25, 70, 25], deal: 9000, dealLabel: "MRR \u2192 ARR" },
  "education-enrolment-calculator": { stages: ["Enquiries", "Applications", "Interviews", "Enrolments"], defs: [400, 40, 70, 50], deal: 180000, dealLabel: "Tuition" },
  "insurance-quote-booking": { stages: ["Enquiries", "Quotes issued", "Policies bound"], defs: [300, 55, 35], deal: 30000, dealLabel: "Premium \u00d7 commission" },
  "legal-consult-booking": { stages: ["Enquiries", "Consults booked", "Attended", "Retained"], defs: [200, 45, 80, 40], deal: 150000, dealLabel: "Retainer" },
  "b2b-lead-qualification-calculator": { stages: ["Chats", "MQL", "SQL", "Opportunity", "Closed-won"], defs: [400, 40, 55, 60, 30], deal: 300000, dealLabel: "ACV \u2192 ARR" },
  "local-service-booking-calculator": { stages: ["Enquiries", "Quotes", "Site visits", "Jobs won"], defs: [200, 60, 70, 55], deal: 45000, dealLabel: "Job value" },
  "automotive-dealership-dm-calculator": { stages: ["Enquiries", "Test drives booked", "Attended", "Units sold"], defs: [300, 30, 75, 35], deal: 90000, dealLabel: "Gross per unit + F&I" },
  "hair-transplant-dm-revenue": { stages: ["Enquiries", "Photo assessments", "Consults booked", "Procedures"], defs: [250, 60, 55, 40], deal: 300000, dealLabel: "Grafts \u00d7 price/graft" },
};
